import type {
  AdminLocation,
  AdminLocationInput,
  AdminProvider,
  AdminProviderInput,
  AffectedAppointment,
  ApiErrorBody,
  AppointmentDetail,
  AppointmentSummary,
  AuditListQuery,
  AuditListResult,
  AvailableCalendar,
  BlockInput,
  BusyCalendarEntry,
  CalendarConfig,
  DocumentViewUrl,
  DocumentDownloadUrl,
  DriftParams,
  DriftResult,
  RetriggerTeamsResult,
  ResendPatientEmailResult,
  PatientEmailStatus,
  ScheduleBlock,
  ScheduleShift,
  ShiftInput,
  TeamsStatus,
  TestTeamsConnectionResult,
  UnacknowledgedRow,
  GoogleConnectionStatus,
  SnapshotParams,
  TroubleshootSnapshot,
} from "./types.js";

export class ApiError extends Error {
  constructor(
    public code: string,
    public status: number,
    message?: string,
    // Populated when the API sends a 409 appointments_affected response —
    // the list of booked appointments a destructive schedule edit would
    // strand. Sent as a sibling of `error` in the body, not nested inside
    // it (see updateShift/deleteShift in apps/api/src/routes/admin/schedule.ts).
    public affected?: AffectedAppointment[],
  ) {
    super(message ?? code);
  }
}

export type GetToken = () => Promise<string>;

export type ListQuery = { from: string; to: string; status?: string };

export type CalendarConfigInput = Omit<
  CalendarConfig,
  "updated_by" | "updated_at"
>;

export type AdminApi = {
  listAppointments(q: ListQuery): Promise<AppointmentSummary[]>;
  // Staff "unacknowledged worklist" — bare JSON array. Backs the Teams tab.
  listUnacknowledged(): Promise<UnacknowledgedRow[]>;
  getAppointment(id: string): Promise<AppointmentDetail>;
  cancelAppointment(id: string, reason: string): Promise<void>;
  retriggerTeamsNotification(id: string): Promise<RetriggerTeamsResult>;
  resendPatientEmail(id: string): Promise<ResendPatientEmailResult>;
  // Emails the patient a tokenised upload link (204 on success).
  requestDocuments(id: string): Promise<void>;
  getDocumentViewUrl(id: string): Promise<DocumentViewUrl>;
  getDocumentDownloadUrl(id: string): Promise<DocumentDownloadUrl>;
  getCalendarConfig(): Promise<CalendarConfig | null>;
  putCalendarConfig(input: CalendarConfigInput): Promise<CalendarConfig>;
  // Audit log — keyset-paginated by occurred_at desc.
  listAuditLog(q: AuditListQuery): Promise<AuditListResult>;
  // Google Calendar OAuth connect/disconnect flow. Calendar selection is
  // a separate step after connect.
  getGoogleStatus(): Promise<GoogleConnectionStatus>;
  startGoogleOAuth(): Promise<{ url: string }>;
  disconnectGoogle(): Promise<void>;
  // Multi-calendar selection: enumerate the user's calendars, then save
  // the practice's chosen busy-source list.
  listAvailableCalendars(): Promise<{ calendars: AvailableCalendar[] }>;
  getBusyCalendars(): Promise<{ calendars: BusyCalendarEntry[] }>;
  putBusyCalendars(
    calendarIds: string[],
  ): Promise<{ calendars: BusyCalendarEntry[] }>;
  getTroubleshootSnapshot(params: SnapshotParams): Promise<TroubleshootSnapshot>;
  runTroubleshootDrift(params: DriftParams): Promise<DriftResult>;
  getTeamsStatus(): Promise<TeamsStatus>;
  testTeamsConnection(): Promise<TestTeamsConnectionResult>;
  sendTestEmail(to: string): Promise<{ status: "sent"; to: string }>;
  getPatientEmailStatus(): Promise<PatientEmailStatus>;
  // Locations (multi-location scheduling).
  listLocations(): Promise<{ locations: AdminLocation[] }>;
  createLocation(input: AdminLocationInput): Promise<AdminLocation>;
  updateLocation(id: string, input: AdminLocationInput): Promise<AdminLocation>;
  // Providers — the scheduling unit; each owns its own Google connection.
  listProviders(): Promise<{ providers: AdminProvider[] }>;
  createProvider(input: AdminProviderInput): Promise<AdminProvider>;
  updateProvider(id: string, input: AdminProviderInput): Promise<AdminProvider>;
  startProviderGoogleOAuth(providerId: string): Promise<{ url: string }>;
  getProviderGoogleStatus(providerId: string): Promise<GoogleConnectionStatus>;
  disconnectProviderGoogle(providerId: string): Promise<void>;
  listProviderAvailableCalendars(
    providerId: string,
  ): Promise<{ calendars: AvailableCalendar[] }>;
  getProviderBusyCalendars(
    providerId: string,
  ): Promise<{ calendars: BusyCalendarEntry[] }>;
  putProviderBusyCalendars(
    providerId: string,
    calendarIds: string[],
  ): Promise<{ calendars: BusyCalendarEntry[] }>;
  // Schedule — shifts (provider+location, availability) and blocks
  // (provider-only, time off).
  getSchedule(q: {
    fromDate: string;
    toDate: string;
    providerId?: string;
  }): Promise<{ shifts: ScheduleShift[]; blocks: ScheduleBlock[] }>;
  createShift(input: ShiftInput): Promise<ScheduleShift>;
  updateShift(
    id: string,
    input: ShiftInput,
    force?: boolean,
  ): Promise<ScheduleShift>;
  deleteShift(id: string, force?: boolean): Promise<void>;
  createBlock(input: BlockInput): Promise<ScheduleBlock>;
  updateBlock(id: string, input: BlockInput): Promise<ScheduleBlock>;
  deleteBlock(id: string): Promise<void>;
};

export function buildAdminApi(baseUrl: string, getToken: GetToken): AdminApi {
  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await getToken();
    const headers = new Headers(init.headers);
    headers.set("authorization", `Bearer ${token}`);
    if (init.body && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }

    let res: Response;
    try {
      res = await fetch(`${baseUrl}${path}`, { ...init, headers });
    } catch (cause) {
      throw new ApiError("network_error", 0, (cause as Error).message);
    }

    const text = await res.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text) as unknown;
      } catch {
        throw new ApiError(
          "invalid_response",
          res.status,
          "API returned a non-JSON error response",
        );
      }
    }

    if (!res.ok) {
      const err = body as ApiErrorBody | { message?: string; statusCode?: number } | null;
      const code =
        err &&
        typeof err === "object" &&
        "error" in err &&
        err.error &&
        typeof err.error === "object" &&
        "code" in err.error &&
        typeof err.error.code === "string"
          ? err.error.code
          : res.status === 404
            ? "route_not_found"
            : "unknown_error";
      const message =
        err &&
        typeof err === "object" &&
        "error" in err &&
        err.error &&
        typeof err.error === "object" &&
        "message" in err.error &&
        typeof err.error.message === "string"
          ? err.error.message
          : err &&
              typeof err === "object" &&
              "message" in err &&
              typeof err.message === "string"
            ? err.message
            : undefined;
      const affected =
        err &&
        typeof err === "object" &&
        "affected" in err &&
        Array.isArray((err as { affected?: unknown }).affected)
          ? ((err as { affected: AffectedAppointment[] }).affected)
          : undefined;
      throw new ApiError(code, res.status, message, affected);
    }
    return body as T;
  }

  return {
    async listAppointments(q) {
      const params = new URLSearchParams({ from: q.from, to: q.to });
      if (q.status) params.set("status", q.status);
      return request<AppointmentSummary[]>(
        `/admin/api/appointments?${params.toString()}`,
      );
    },
    async listUnacknowledged() {
      return request<UnacknowledgedRow[]>(
        `/admin/api/appointments/unacknowledged`,
      );
    },
    async getAppointment(id) {
      return request<AppointmentDetail>(`/admin/api/appointments/${id}`);
    },
    async cancelAppointment(id, reason) {
      await request<unknown>(`/admin/api/appointments/${id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
    },
    async retriggerTeamsNotification(id) {
      return request<RetriggerTeamsResult>(
        `/admin/api/appointments/${id}/retrigger-teams`,
        { method: "POST" },
      );
    },
    async resendPatientEmail(id) {
      return request<ResendPatientEmailResult>(
        `/admin/api/appointments/${id}/resend-email`,
        { method: "POST" },
      );
    },
    async requestDocuments(id) {
      await request<void>(`/admin/api/appointments/${id}/request-documents`, {
        method: "POST",
      });
    },
    async getDocumentViewUrl(id) {
      return request<DocumentViewUrl>(
        `/admin/api/documents/${id}/view-url`,
      );
    },
    async getDocumentDownloadUrl(id) {
      return request<DocumentDownloadUrl>(
        `/admin/api/documents/${id}/download-url`,
      );
    },
    async getCalendarConfig() {
      try {
        return await request<CalendarConfig>(`/admin/api/calendar-config`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    async putCalendarConfig(input) {
      return request<CalendarConfig>(`/admin/api/calendar-config`, {
        method: "PUT",
        body: JSON.stringify(input),
      });
    },
    async listAuditLog(q) {
      const params = new URLSearchParams();
      if (q.before) params.set("before", q.before);
      if (q.limit !== undefined) params.set("limit", String(q.limit));
      if (q.action) params.set("action", q.action);
      if (q.actor) params.set("actor", q.actor);
      const qs = params.toString();
      return request<AuditListResult>(
        `/admin/api/audit-log${qs ? `?${qs}` : ""}`,
      );
    },
    async getGoogleStatus() {
      return request<GoogleConnectionStatus>(
        `/admin/api/calendar/google/status`,
      );
    },
    async startGoogleOAuth() {
      return request<{ url: string }>(
        `/admin/api/calendar/google/authorize`,
        { method: "POST" },
      );
    },
    async disconnectGoogle() {
      await request<unknown>(`/admin/api/calendar/google/connection`, {
        method: "DELETE",
      });
    },
    async listAvailableCalendars() {
      return request<{ calendars: AvailableCalendar[] }>(
        `/admin/api/calendar/google/available-calendars`,
      );
    },
    async getBusyCalendars() {
      return request<{ calendars: BusyCalendarEntry[] }>(
        `/admin/api/calendar/busy-calendars`,
      );
    },
    async putBusyCalendars(calendarIds) {
      return request<{ calendars: BusyCalendarEntry[] }>(
        `/admin/api/calendar/busy-calendars`,
        {
          method: "PUT",
          body: JSON.stringify({ calendar_ids: calendarIds }),
        },
      );
    },
    async getTroubleshootSnapshot(params) {
      const qs = new URLSearchParams({
        fromDate: params.fromDate,
        toDate: params.toDate,
        durationMinutes: String(params.durationMinutes),
      });
      return request<TroubleshootSnapshot>(
        `/admin/api/troubleshoot/snapshot?${qs.toString()}`,
      );
    },
    async runTroubleshootDrift(params) {
      const qs = new URLSearchParams({
        fromDate: params.fromDate,
        toDate: params.toDate,
      });
      return request<DriftResult>(
        `/admin/api/troubleshoot/drift?${qs.toString()}`,
        { method: "POST" },
      );
    },
    async getTeamsStatus() {
      return request<TeamsStatus>(`/admin/api/teams/status`);
    },
    async testTeamsConnection() {
      return request<TestTeamsConnectionResult>(`/admin/api/teams/test`, {
        method: "POST",
      });
    },
    async sendTestEmail(to) {
      return request<{ status: "sent"; to: string }>(`/admin/api/email/test`, {
        method: "POST",
        body: JSON.stringify({ to }),
      });
    },
    async getPatientEmailStatus() {
      return request<PatientEmailStatus>(`/admin/api/email/status`);
    },
    async listLocations() {
      return request<{ locations: AdminLocation[] }>(`/admin/api/locations`);
    },
    async createLocation(input) {
      return request<AdminLocation>(`/admin/api/locations`, {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    async updateLocation(id, input) {
      return request<AdminLocation>(`/admin/api/locations/${id}`, {
        method: "PUT",
        body: JSON.stringify(input),
      });
    },
    async listProviders() {
      return request<{ providers: AdminProvider[] }>(`/admin/api/providers`);
    },
    async createProvider(input) {
      return request<AdminProvider>(`/admin/api/providers`, {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    async updateProvider(id, input) {
      return request<AdminProvider>(`/admin/api/providers/${id}`, {
        method: "PUT",
        body: JSON.stringify(input),
      });
    },
    async startProviderGoogleOAuth(providerId) {
      return request<{ url: string }>(
        `/admin/api/providers/${providerId}/google/authorize`,
        { method: "POST" },
      );
    },
    async getProviderGoogleStatus(providerId) {
      return request<GoogleConnectionStatus>(
        `/admin/api/providers/${providerId}/google/status`,
      );
    },
    async disconnectProviderGoogle(providerId) {
      await request<unknown>(
        `/admin/api/providers/${providerId}/google/connection`,
        { method: "DELETE" },
      );
    },
    async listProviderAvailableCalendars(providerId) {
      return request<{ calendars: AvailableCalendar[] }>(
        `/admin/api/providers/${providerId}/google/available-calendars`,
      );
    },
    async getProviderBusyCalendars(providerId) {
      return request<{ calendars: BusyCalendarEntry[] }>(
        `/admin/api/providers/${providerId}/busy-calendars`,
      );
    },
    async putProviderBusyCalendars(providerId, calendarIds) {
      return request<{ calendars: BusyCalendarEntry[] }>(
        `/admin/api/providers/${providerId}/busy-calendars`,
        {
          method: "PUT",
          body: JSON.stringify({ calendar_ids: calendarIds }),
        },
      );
    },
    async getSchedule(q) {
      const params = new URLSearchParams({
        fromDate: q.fromDate,
        toDate: q.toDate,
      });
      if (q.providerId) params.set("providerId", q.providerId);
      return request<{ shifts: ScheduleShift[]; blocks: ScheduleBlock[] }>(
        `/admin/api/schedule?${params.toString()}`,
      );
    },
    async createShift(input) {
      return request<ScheduleShift>(`/admin/api/schedule/shifts`, {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    async updateShift(id, input, force) {
      const qs = force ? "?force=true" : "";
      return request<ScheduleShift>(`/admin/api/schedule/shifts/${id}${qs}`, {
        method: "PUT",
        body: JSON.stringify(input),
      });
    },
    async deleteShift(id, force) {
      const qs = force ? "?force=true" : "";
      await request<unknown>(`/admin/api/schedule/shifts/${id}${qs}`, {
        method: "DELETE",
      });
    },
    async createBlock(input) {
      // BlockBody on the API validates label with `.optional()`, not
      // `.nullable()` — an explicit `label: null` fails zod parsing (400).
      // Drop the key entirely when there's no label so JSON.stringify
      // serializes it as absent rather than null.
      const { label, ...rest } = input;
      return request<ScheduleBlock>(`/admin/api/schedule/blocks`, {
        method: "POST",
        body: JSON.stringify(label == null ? rest : { ...rest, label }),
      });
    },
    async updateBlock(id, input) {
      const { label, ...rest } = input;
      return request<ScheduleBlock>(`/admin/api/schedule/blocks/${id}`, {
        method: "PUT",
        body: JSON.stringify(label == null ? rest : { ...rest, label }),
      });
    },
    async deleteBlock(id) {
      await request<unknown>(`/admin/api/schedule/blocks/${id}`, {
        method: "DELETE",
      });
    },
  };
}
